/**
 * The berth tab's model (M8 plan MC-b6a; D-486 the store, D-500 its access rules): what is in the hold that can be
 * stored, what is stored here that can be taken, and the berth's state from `berthAccess` ("Frozen: station disabled",
 * "Relocating to X on day N"). Pure; `ui/berth/Berth.tsx` renders it and `features/berth/feature.ts` runs the
 * actions through `UiActions.craft`.
 *
 * - **Local actions only when docked there:** every button is disabled (with a reason) unless the player is docked at
 *   the berth's station and the berth is open.
 * - **Refused kinds** (contract, sealed, story, contraband) are listed with `berthRefusal`'s reason and no button
 *   (`action` false, PF-5: they can never be stored, so the reason beats a closed-berth one); the
 *   component caps disable storing more components; a take is limited to what the free hold and the cargo racks take.
 */
import { z } from 'zod'
import {
  BERTH_REFUSAL_TEXT,
  berthAccess,
  berthAt,
  berthComponentCount,
  berthRefusal,
  relocationTarget,
  type BerthAccess,
  type BerthData,
  type BerthSelection,
  type BerthStationView,
} from '../../career/berth'
import { EPSILON, GOOD_NAMES, cargoFreeT } from '../../career/ops'
import { GOOD_IDS, GOOD_MASS_T, goodT, type GoodId } from '../../career/parts'
import type { BerthEntry, CargoComponent, Career } from '../../career/types'
import { moduleStats } from '../../data/m2'
import { WARE_IDS, type M2Data, type StationId, type WareId } from '../../data/m2Schemas'
import strategicJson from '../../data/strategic/strategic.json'
import type { CorpFactionId, StationView } from '../../strategic/types'
import berthJson from '../berth/berth.json'
import { formatTonnes } from '../format'
import { wareName } from './market'

const textSchema = z.strictObject({
  title: z.string().min(1),
  sub: z.string(),
  open: z.string().min(1),
  frozen: z.string().min(1),
  relocatingTo: z.string().min(1),
  relocating: z.string().min(1),
  notDocked: z.string().min(1),
  noCareer: z.string().min(1),
  holdHeading: z.string().min(1),
  berthHeading: z.string().min(1),
  holdEmpty: z.string().min(1),
  berthEmpty: z.string().min(1),
  components: z.string().min(1),
  refused: z.string().min(1),
  stationFull: z.string().min(1),
  allFull: z.string().min(1),
  tooLarge: z.string().min(1),
  holdFull: z.string().min(1),
  heldForFab: z.string().min(1),
  heldForFabRefused: z.string().min(1),
  store: z.string().min(1),
  take: z.string().min(1),
  stored: z.string().min(1),
  taken: z.string().min(1),
})

const berthUiSchema = z.strictObject({ comment: z.string(), order: z.number(), text: textSchema })
export type BerthUiConfig = z.infer<typeof berthUiSchema>
export type BerthText = BerthUiConfig['text']

/** The tab's config (`ui/berth/berth.json`), parsed once. */
export const BERTH_UI: Readonly<BerthUiConfig> = Object.freeze(berthUiSchema.parse(berthJson))

/** Game seconds per day for "on day N" (the strategic faction day, as `relocateAfter` is stamped). */
const DAY_S: number = strategicJson.clock.dayS

/** Fills `{key}` placeholders. */
function fill(template: string, vals: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vals ? String(vals[k]) : m))
}

/** The open fab order holding berthed component `componentId` (MC-c8, `FabOrder.salvageIn`), or null. */
export function fabOrderHolding(career: Pick<Career, 'fabOrders'>, componentId: string): string | null {
  for (const o of career.fabOrders ?? []) if (o.salvageIn.includes(componentId)) return o.id
  return null
}

/**
 * The berth's access at `stationId`: `berthAccess` over the berth there, or over an empty berth stamped `owner` when
 * nothing is stored yet (so a disabled station freezes a first store too). `view`: the station's cached view, or null.
 */
export function berthAccessAt(career: Career, data: BerthData, stationId: StationId, view: BerthStationView | null, owner: CorpFactionId, now: number): BerthAccess {
  const entry: BerthEntry = berthAt(career, stationId) ?? { owner, goods: {}, wares: {}, components: [] }
  return berthAccess(entry, view, career, now, data)
}

export interface BerthModelInput {
  data: M2Data
  career: Career | null
  /** The berth's station (the career station id the berth is keyed by). */
  stationId: StationId
  /** The player is docked at `stationId` (local actions only then). */
  docked: boolean
  /** The station's cached view, or null (no strategic link, or not listed yet). */
  view: StationView | null
  /** The system's cached station views (the relocation target and its name), or none. */
  stations: readonly StationView[]
  /** The owner a first deposit is stamped with (`berthOwner`). */
  owner: CorpFactionId
  /** Game time now (seconds). */
  now: number
  text?: BerthText
}

export interface BerthRow {
  /** `good:<id>`, `ware:<id>` or `comp:<id>` (unique within its list). */
  key: string
  name: string
  amountLabel: string
  /** What the button moves. */
  sel: BerthSelection
  enabled: boolean
  /** Why it is disabled ('' when enabled). */
  reason: string
  /**
   * False for a refused kind (contract, sealed, story, contraband: it can never be stored, PF-5): the row shows its
   * reason and no button. True otherwise, the button disabled while `enabled` is false.
   */
  action: boolean
}

export interface BerthModel {
  title: string
  sub: string
  access: BerthAccess['kind']
  /** The state line ("Open", "Frozen: station disabled", "Relocating to X on day N"). */
  stateLabel: string
  /** Why every action is closed (not docked, frozen, relocating), else null. */
  closedReason: string | null
  componentsLabel: string
  hold: BerthRow[]
  stored: BerthRow[]
  holdEmpty: string | null
  storedEmpty: string | null
  holdHeading: string
  berthHeading: string
  storeLabel: string
  takeLabel: string
}

const MK_NAMES: readonly string[] = Object.freeze(['I', 'II', 'III'])

function componentName(data: M2Data, c: CargoComponent): string {
  const def = data.wares.components.find((d) => d.type === c.type)
  const name = def?.name ?? c.type
  const mk = c.mk === undefined ? undefined : MK_NAMES[c.mk - 1]
  return mk === undefined ? name : `${name} Mk ${mk}`
}

/** Rounds down to a tenth (a take never asks for more than fits). */
function floorTenth(t: number): number {
  return Math.floor(t * 10 + EPSILON) / 10
}

function stateLine(access: BerthAccess, input: BerthModelInput, career: Career, text: BerthText): string {
  if (access.kind === 'open') return text.open
  if (access.kind === 'frozen') return text.frozen
  const day = Math.floor(access.dueTime / DAY_S)
  const target = input.view === null ? null : relocationTarget(input.view, { stations: input.stations, hops: () => -1 }, career)
  if (target === null) return fill(text.relocating, { day })
  const name = input.stations.find((s) => s.id === target.view.id)?.name ?? target.view.id
  return fill(text.relocatingTo, { station: name, day })
}

/** The berth tab's model. */
export function berthModel(input: BerthModelInput): BerthModel {
  const text = input.text ?? BERTH_UI.text
  const base = {
    title: text.title,
    sub: text.sub,
    holdHeading: text.holdHeading,
    berthHeading: text.berthHeading,
    storeLabel: text.store,
    takeLabel: text.take,
  }
  const career = input.career
  if (career === null) {
    return { ...base, access: 'open', stateLabel: text.open, closedReason: text.noCareer, componentsLabel: '', hold: [], stored: [], holdEmpty: null, storedEmpty: null }
  }
  const { data, stationId } = input
  const access = berthAccessAt(career, data, stationId, input.view, input.owner, input.now)
  const stateLabel = stateLine(access, input, career, text)
  const closedReason = !input.docked ? text.notDocked : access.kind === 'open' ? null : stateLabel
  const closed = closedReason !== null

  const caps = data.career.berth
  const entry = berthAt(career, stationId)
  const here = entry?.components.length ?? 0
  const total = berthComponentCount(career)
  const componentsLabel = fill(text.components, { here, capHere: caps.componentCapPerStation, total, capTotal: caps.componentCapTotal })

  // ---- The hold: what can be stored ----
  const hold: BerthRow[] = []
  const row = (key: string, name: string, amountLabel: string, sel: BerthSelection, reason: string | null, refused = false): BerthRow => {
    const why = refused ? (reason ?? '') : (closedReason ?? reason ?? '')
    return { key, name, amountLabel, sel, enabled: why === '', reason: why, action: !refused }
  }
  for (const g of GOOD_IDS) {
    const q = goodT(career.cargo.parts, g)
    if (q > EPSILON) hold.push(row(`good:${g}`, GOOD_NAMES[g], `${formatTonnes(q)} t`, { goods: { [g]: q } }, null))
  }
  for (const w of WARE_IDS) {
    const t = career.cargo.wares[w]
    if (t > EPSILON) hold.push(row(`ware:${w}`, wareName(data, career, w), `${formatTonnes(t)} t`, { wares: { [w]: t } }, null))
  }
  const capReason = here >= caps.componentCapPerStation ? text.stationFull : total >= caps.componentCapTotal ? text.allFull : null
  for (const c of career.cargo.components) {
    const refusal = berthRefusal(c)
    const reason = refusal !== null ? fill(text.refused, { what: BERTH_REFUSAL_TEXT[refusal] }) : capReason
    hold.push(row(`comp:${c.id}`, componentName(data, c), `${formatTonnes(c.massT)} t`, { components: [c.id] }, reason, refusal !== null))
  }

  // ---- The berth: what can be taken ----
  const stored: BerthRow[] = []
  const free = cargoFreeT(career)
  if (entry !== null) {
    const maxPartT = moduleStats(data, 'cargo', career.ship.fitted.cargo).maxPartMassT
    const takeRow = (key: string, name: string, have: number, massPerUnit: number, sel: (q: number) => BerthSelection): BerthRow => {
      const fit = floorTenth(free / massPerUnit)
      const q = have <= fit + EPSILON ? have : fit
      return row(key, name, `${formatTonnes(have)} t`, sel(q), q > EPSILON ? null : text.holdFull)
    }
    for (const g of GOOD_IDS) {
      const q = goodT(entry.goods, g)
      if (q > EPSILON) stored.push(takeRow(`good:${g}`, GOOD_NAMES[g], q, GOOD_MASS_T[g], (n): BerthSelection => ({ goods: { [g as GoodId]: n } })))
    }
    for (const w of WARE_IDS) {
      const t = entry.wares[w] ?? 0
      if (t > EPSILON) stored.push(takeRow(`ware:${w}`, wareName(data, career, w), t, 1, (n): BerthSelection => ({ wares: { [w as WareId]: n } })))
    }
    for (const c of entry.components) {
      const reason = fabOrderHolding(career, c.id) !== null ? text.heldForFab : c.massT > maxPartT + EPSILON ? text.tooLarge : c.massT > free + EPSILON ? text.holdFull : null
      stored.push(row(`comp:${c.id}`, componentName(data, c), `${formatTonnes(c.massT)} t`, { components: [c.id] }, reason))
    }
  }

  return {
    ...base,
    access: access.kind,
    stateLabel,
    closedReason: closed ? closedReason : null,
    componentsLabel,
    hold,
    stored,
    holdEmpty: hold.length === 0 ? text.holdEmpty : null,
    storedEmpty: stored.length === 0 ? text.berthEmpty : null,
  }
}
